import { RegularMetricId } from '../../../api/types-graphql';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { KpiFactboxTwoMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-two-metric/types';
import { OverTimeUnsegmentedVizViews } from '../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { OverTimeSegmentedVizViews } from '../../../common/components/data-viz-containers/overtime-segmented-query/types';
import { TimePeriodSegmentedVizViews } from '../../../common/components/data-viz-containers/time-period-segmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../constants';
import { getMomentDate } from '../../../date/date-utils';
import {
  ApplicationDataFields,
  DataTypes,
  EmployeeDataFields,
  TimeSelectionType,
} from '../../../types';
import { StoryboardDateUtils } from '../utils';

const pastelColors = [
  '#FF9999', // Brighter Pink
  '#99CC99', // Brighter Green
  '#99CCFF', // Brighter Blue
  '#FFFF99', // Brighter Yellow
  '#FFCC99', // Brighter Orange
  '#CC99FF', // Brighter Purple
  '#99E6CC', // Brighter Mint
  '#FF99CC', // Brighter Rose
  '#CC99FF', // Brighter Lavender
  '#E6CC99', // Brighter Beige
];

export const getAttritionChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'attrition-ttm',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0076_3AttritionTtm,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearYearly,
          input: { start: 2022, end: 2023 },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getMultiMetricLineChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'multi-metric-line',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0019_2AgeAvg,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0010_1MngCalcCount,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0020_1FhcCount,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0023_1TenureMedian,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: {
            start: { quarterOfYear: 2, year: 2022 },
            end: { quarterOfYear: 3, year: 2023 },
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getJoinersOverTimeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'joiners',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0006_1JoinersJoiners,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearYearly,
          input: { start: 2020, end: 2023 },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.LOCATION_LEVEL_1,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getIndividualsCountChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'individuals-count',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: { start: '2022-08-31', end: '2022-08-31' },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getNewJoinersManagersByGenderChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'new-joiners-managers-by-gender',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0014_1MngDefCount,
          },
          filters: `EMPLOYEE.TENURE_GROUP = ('6ヶ月未満')`,
        },
        timeSelection: {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: {
            start: { quarterOfYear: 2, year: 2022 },
            end: { quarterOfYear: 3, year: 2023 },
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.GENDER,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getLeaversByGenderChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'leavers-by-gender',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0076_4AttritionLeavers,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: { start: '2022-06-01', end: '2023-09-30' },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.GENDER,
          },
          filters: ['Male', 'Female'],
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.BAR_CHART,
    },
  };
};

export const getFemaleManagersChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: 'female-managers',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0016_1FMngDefCount,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0016_2FMngDefPercHc,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getParentalLeaveFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'parental-leave-utilization-by-gender',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0171ParentalLeaveTakenUtilizationPerc,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: { start: '2021-01-01', end: '2021-12-31' },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.GENDER,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getIndividualsFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'individuals-count-factbox',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Individuals Count',
          [Languages.JA]: '従業員数',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getJoinersLeaversFactbox = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'joiners-and-leavers-count-factbox',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Joiners & Leavers Count',
          [Languages.JA]: '入社者と退職者数',
        },
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: 'Joiners',
              [Languages.JA]: '入社者',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0006_1JoinersJoiners,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: { start: nowEndOfMonth, end: nowEndOfMonth },
              },
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: 'Leavers',
              [Languages.JA]: '退職者',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0076_4AttritionLeavers,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: { start: nowEndOfMonth, end: nowEndOfMonth },
              },
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
};

// Additional helper functions for missing charts

export const getNewManagersByGenderChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'new-managers-by-gender',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'New Managers by Gender',
          [Languages.JA]: '新しいマネージャーの性別',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0015_1NewMngDefCount,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearYearly,
          input: {
            start: getMomentDate(dateUtils.now).year() - 5,
            end: getMomentDate(dateUtils.now).year(),
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.GENDER,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getFemaleManagersByDepartmentChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'female-managers-by-department',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Female Managers by Department',
          [Languages.JA]: '部門別女性マネージャー',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0016_1FMngDefCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: { start: nowEndOfMonth, end: nowEndOfMonth },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getManagementPromotionChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'management-promotion-pct-hc',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Management Promotion % of HC',
          [Languages.JA]: '管理職昇進率',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0196ManagementPromotionPercOfHeadcount,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearYearly,
          input: {
            start: getMomentDate(dateUtils.now).year() - 5,
            end: getMomentDate(dateUtils.now).year(),
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.GENDER,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getApplicationsByGenderChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'applications-by-gender',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Applications by Gender',
          [Languages.JA]: '応募者の性別',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0252_2ApplicationsCount,
          },
        },
        timeSelection: {
          type: TimeSelectionType.FinancialYearYearly,
          input: {
            start: getMomentDate(dateUtils.now).year() - 2,
            end: getMomentDate(dateUtils.now).year(),
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.APPLICATION,
            dataField: ApplicationDataFields.CUSTOM_FILTER_1,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getApplicationsBySourceChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'applications-by-source',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Applications by Source',
          [Languages.JA]: '応募者のソース',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0252_2ApplicationsCount,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearYearly,
          input: { start: 2019, end: 2021 },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.APPLICATION,
            dataField: ApplicationDataFields.SOURCE_LEVEL_1,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getIndividualsByDeptAndAgeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'individuals-by-department-by-age-group',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Individuals by Department and by Age Group',
          [Languages.JA]: '部門別年齢層別の従業員数',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
          timeSelection: {
            type: TimeSelectionType.CalendarYearSingleValueByMonths,
            input: { start: nowEndOfMonth, end: nowEndOfMonth },
          },
          groupBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.LOCATION_LEVEL_1,
            },
          },
          segmentBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.AGE_GROUP,
            },
          },
        },
        chartColors: pastelColors,
      },
      selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
    },
  };
};

export const getJoinersLeaversOverTimeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: 'joiners-and-leavers-count-over-time',
    layoutConfig: {
      gridColSpan: 4,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Joiners & Leavers Over Time',
          [Languages.JA]: '入社者と退職者の推移',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0006_1JoinersJoiners,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0076_4AttritionLeavers,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getJoinersLeaversBySegmentChart = (
  dateUtils: StoryboardDateUtils,
  segmentField: EmployeeDataFields,
  chartId: string,
  title: Record<Languages, string>,
  gridColSpan = 1
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId,
    layoutConfig: {
      gridColSpan,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title,
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0006_1JoinersJoiners,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0076_4AttritionLeavers,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: segmentField,
          },
        },
        chartColors: pastelColors,
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getHeadcountOverTimeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } = dateUtils;
  return {
    chartId: 'people-snapshot-headcount-over-time',
    layoutConfig: {
      gridColSpan: 3,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Headcount Over Time',
          [Languages.JA]: '従業員数の推移',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getHeadcountByEmploymentTypeChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'people-snapshot-headcount-by-employment-type',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Employment Type',
          [Languages.JA]: '雇用形態',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: { start: nowEndOfMonth, end: nowEndOfMonth },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getHeadcountByYearChart = (
  dateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return {
    chartId: 'people-snapshot-headcount-by-year',
    layoutConfig: {
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Headcount by Year',
          [Languages.JA]: '年別従業員数',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearYearly,
          input: {
            start: getMomentDate(dateUtils.now).year() - 14,
            end: getMomentDate(dateUtils.now).year(),
          },
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getDemographicBySegmentChart = (
  dateUtils: StoryboardDateUtils,
  segmentField: EmployeeDataFields,
  chartId: string,
  title: Record<Languages, string>,
  gridColStart?: number,
  gridColEnd?: number,
  gridRowStart?: number,
  gridRowEnd?: number,
  isPieChart?: boolean
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  const selectedView = isPieChart
    ? TimePeriodUnsegmentedVizViews.PIE_CHART
    : TimePeriodUnsegmentedVizViews.BAR_CHART;
  return {
    chartId,
    layoutConfig: {
      gridColStart,
      gridColEnd,
      gridRowStart,
      gridRowEnd,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title,
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: { start: nowEndOfMonth, end: nowEndOfMonth },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: segmentField,
          },
        },
        chartColors:
          segmentField === EmployeeDataFields.GENDER
            ? ['#7245b2', '#25b1e4']
            : undefined,
      },
      selectedView,
    },
  };
};

export const getTenureFactbox = (
  dateUtils: StoryboardDateUtils,
  gridColStart?: number,
  gridColEnd?: number,
  gridRowStart?: number,
  gridRowEnd?: number
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'people-snapshot-demographic-factbox-tenure',
    layoutConfig: {
      gridColStart,
      gridColEnd,
      gridRowStart,
      gridRowEnd,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0023_2TenureAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
          },
          benchmarkConfigs: [],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getAgeFactbox = (
  dateUtils: StoryboardDateUtils,
  gridColStart?: number,
  gridColEnd?: number,
  gridRowStart?: number,
  gridRowEnd?: number
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'people-snapshot-demographic-factbox-age',
    layoutConfig: {
      gridColStart,
      gridColEnd,
      gridRowStart,
      gridRowEnd,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0019_2AgeAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
          },
          benchmarkConfigs: [],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getHeadcountFactboxWithGrid = (
  dateUtils: StoryboardDateUtils,
  gridColStart?: number,
  gridColEnd?: number,
  gridRowStart?: number,
  gridRowEnd?: number
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'people-snapshot-demographic-factbox-hc',
    layoutConfig: {
      gridColStart,
      gridColEnd,
      gridRowStart,
      gridRowEnd,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
          },
          benchmarkConfigs: [],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getHeadcountFactboxSegmentWithGrid = (
  dateUtils: StoryboardDateUtils,
  gridColStart?: number,
  gridColEnd?: number,
  gridRowStart?: number,
  gridRowEnd?: number
): StoryboardItemConfig => {
  const { nowEndOfMonth } = dateUtils;
  return {
    chartId: 'people-snapshot-demographic-factbox-hc',
    layoutConfig: {
      gridColStart,
      gridColEnd,
      gridRowStart,
      gridRowEnd,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
            segmentBy: {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.GENDER,
              },
            },
          },
          benchmarkConfigs: [],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};
